import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { CheckCircle, Calendar, Mail, Phone } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useNoIndex } from '../lib/seoHelpers';

const ThankYouPage = () => {
  useNoIndex();
  const [searchParams] = useSearchParams();
  const [tracked, setTracked] = useState(false);
  
  const plan = searchParams.get('plan');
  const isStandard = plan === 'standard';
  const isPriority = plan === 'priority';
  
  // Track purchase conversion on page load
  useEffect(() => {
    if (tracked) return;
    
    if (plan && window.sdcTrack) {
      if (isStandard) {
        window.sdcTrack('purchase_standard', { value: 777, currency: 'USD' });
      } else if (isPriority) {
        window.sdcTrack('purchase_priority', { value: 999, currency: 'USD' });
      }
      setTracked(true);
    }
  }, [plan, isStandard, isPriority, tracked]);

  return (
    <div className="min-h-screen bg-[#1a1c1b]">
      <Header />
      
      <main className="pt-32 pb-16">
        <div className="max-w-2xl mx-auto px-5 md:px-10 text-center">
          {/* Success Icon */}
          <div className="w-24 h-24 mx-auto rounded-full bg-[#3f4816] flex items-center justify-center mb-8">
            <CheckCircle className="w-12 h-12 text-[#d9fb06]" />
          </div>
          
          <h1 className="text-3xl md:text-4xl font-black text-[#d9fb06] uppercase mb-4">
            Thank You!
          </h1>
          
          {plan ? (
            <p className="text-lg text-[#888680] mb-8">
              Your <span className="text-[#dfddd6] font-semibold">
                {isStandard ? 'Standard' : 'Priority'}
              </span> package payment has been received. We'll email you next steps within 24 hours.
            </p>
          ) : (
            <p className="text-lg text-[#888680] mb-8">
              Your payment has been received. We'll email you next steps within 24 hours.
            </p>
          )}
          
          {/* What Happens Next */}
          <div className="bg-[#302f2c] border border-[#3f4816]/50 rounded-2xl p-8 mb-8 text-left">
            <h2 className="text-xl font-bold text-[#dfddd6] mb-4">What Happens Next</h2>
            <ol className="space-y-4">
              <li className="flex items-start gap-3">
                <span className="w-6 h-6 rounded-full bg-[#d9fb06] text-[#1a1c1b] flex items-center justify-center text-sm font-bold flex-shrink-0">1</span>
                <div>
                  <p className="text-[#dfddd6] font-medium">Case Review</p>
                  <p className="text-[#888680] text-sm">Our team will review your intake information within 24 hours.</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-6 h-6 rounded-full bg-[#d9fb06] text-[#1a1c1b] flex items-center justify-center text-sm font-bold flex-shrink-0">2</span>
                <div>
                  <p className="text-[#dfddd6] font-medium">Documentation Preparation</p>
                  <p className="text-[#888680] text-sm">We'll prepare your documentation ({isPriority ? 'expedited' : 'standard timeline'}).</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-6 h-6 rounded-full bg-[#d9fb06] text-[#1a1c1b] flex items-center justify-center text-sm font-bold flex-shrink-0">3</span>
                <div>
                  <p className="text-[#dfddd6] font-medium">Submission & Tracking</p>
                  <p className="text-[#888680] text-sm">Documentation prepared for submission through official appeal channels.</p>
                </div>
              </li>
            </ol>
          </div>
          
          {/* Contact Info */}
          <div className="bg-[#1a1c1b] border border-[#3f4816]/50 rounded-2xl p-6 mb-8">
            <h3 className="text-lg font-bold text-[#dfddd6] mb-4">Questions?</h3>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="mailto:intake@socialdisputeco.com" 
                className="flex items-center justify-center gap-2 text-[#888680] hover:text-[#d9fb06] transition-colors"
              >
                <Mail className="w-4 h-4" />
                intake@socialdisputeco.com
              </a>
              <a 
                href="tel:808-301-2134" 
                className="flex items-center justify-center gap-2 text-[#888680] hover:text-[#d9fb06] transition-colors"
              >
                <Phone className="w-4 h-4" />
                808-301-2134
              </a>
            </div>
          </div>
          
          {/* Free Suspension Analyzer prompt */}
          <div className="mb-8">
            <Link
              to="/analyze"
              className="inline-flex items-center gap-2 bg-[#d9fb06] text-[#1a1c1b] px-8 py-4 rounded-full font-bold uppercase hover:bg-[#d9fb06]/90 transition-colors"
              data-sdc-event="cta_analyze"
              data-sdc-label="thank_you_page"
            >
              <Calendar className="w-5 h-5" />
              Try the Free Suspension Analyzer
            </Link>
          </div>
          
          <Link
            to="/"
            className="text-[#888680] hover:text-[#d9fb06] transition-colors text-sm"
          >
            ← Return to Home
          </Link>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default ThankYouPage;
